import { Box, Typography, useTheme, Button } from "@mui/material";
import { tokens } from "../../contexts/theme";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from "../headers/header";
import FormatLineChart from "../../visualizations/FormatLineChart";
import { GetRecentData } from "../../forge/main";
import ChangeDate from "../global/ChangeDate";

const Dashboard = ({ viewer, urn }) => {
  const [data, setData] = useState([]);
  // const [timeData, setTimeData] = useState([]);
  const [expanded, setExpanded] = useState(false);
  // const Autodesk = window.Autodesk;


  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const title_str = "DASHBOARD";
  const subtitle_str = [
    "모니터링 시스템을 요약적으로 보여주는 화면입니다. 마지막으로 측정된 데이터를 보여줍니다."
  ];

  const getMaxSum = (lists) => {
    const maxSum = lists.reduce((max, item) => {
      const sum = item["Tunnel-1"] + item["Tunnel-2"];
      return sum > max ? sum : max;
    }, -Infinity);

    return maxSum;
  };

  let sensors = useMemo(() => [
    {
      name: "Activity",
      description: "창문의 움직임을 측정합니다.",
      unit: "단위 없음",
      lastMeasurement1: undefined,
      lastMeasurement2: "센서 없음",
    },
    {
      name: "AirQuality(PM2.5)",
      description: "공기 질을 나타내는 농도를 측정합니다. Dashboard에서는 PM2.5만 표시합니다.",
      unit: "µg/m³",
      lastMeasurement1: undefined,
      lastMeasurement2: undefined,
    },
    {
      name: "LightSensor",
      description: "조도를 측정하는 센서입니다.",
      unit: "Lux",
      lastMeasurement1: undefined,
      lastMeasurement2: undefined,
    },
    {
      name: "Temperature",
      description: "온도를 측정하는 센서입니다.",
      unit: "°C",
      lastMeasurement1: undefined,
      lastMeasurement2: undefined,
    },
    {
      name: "TiltDetection",
      description: "기울기를 측정하는 센서입니다.",
      unit: "°",
      lastMeasurement1: undefined,
      lastMeasurement2: undefined,
    },
    {
      name: "Vibration(Frequency-X)",
      description: "진동을 측정하는 센서입니다. Dashboard에서는 x축 진동수만 표시합니다.",
      unit: "Hz",
      lastMeasurement1: undefined,
      lastMeasurement2: undefined,
    },
    {
      name: "WaterDetection",
      description: "물의 존재 여부를 탐지합니다",
      unit: "단위 없음",
      lastMeasurement1: undefined,
      lastMeasurement2: undefined,
    }
  ], []);

  const AllData = useCallback((lists) => {
    let FirstData = {
      "Sensor": undefined,
      "Tunnel-1": undefined,
      "Tunnel-1Color": "hsl(251, 70%, 50%)",
      "Tunnel-2": undefined,
      "Tunnel-2Color": "hsl(201, 70%, 50%)"
    };

    let newLists = [];
    let newData_Act = {...FirstData};
    let newData_Air = {...FirstData};
    let newData_Light = {...FirstData};
    let newData_Tilt = {...FirstData};
    let newData_Vib = {...FirstData};
    let newData_Temp = {...FirstData};
    let newData_Water = {...FirstData};

    for(let i=0; i<lists.length; i++){
      if (typeof lists[i] !== 'object' || lists[i] === null) continue;
      if(!('SensorName' in lists[i])) continue;

      if('LastCommunicationDate' in lists[i]){
        lists[i].LastCommunicationDate = ChangeDate(lists[i].LastCommunicationDate);
      }

      const name = lists[i].SensorName;
      const currentData = lists[i].CurrentReading;
      const time = lists[i].LastCommunicationDate;
      
      if(name.includes('Air Quality')){
        if(newData_Air.Sensor === undefined) newData_Air.Sensor = "AirQuality(PM2.5)";
        
        const regex = /PM2\.5:\s*(\d+)\s*μg\/m\^3/;
        const match = currentData.match(regex);
        let value = 0
        if (match) value = parseFloat(match[1]);

        if(name.includes('_01')){
          newData_Air["Tunnel-1"] = value;
          sensors[1].lastMeasurement1 = time;
        }
        else{
          newData_Air["Tunnel-2"] = value;
          sensors[1].lastMeasurement2 = time;
        }
      }
      else if(name.includes('Activity')){
        newData_Act.Sensor = "Activity";
        if(currentData === 'No Motion Detected') newData_Act["Tunnel-1"] = 0;
        else newData_Act["Tunnel-1"] = 1;
        newData_Act["Tunnel-2"] = 0;
        sensors[0].lastMeasurement1 = time;
      }
      else if(name.includes('Vibration')){
        if(newData_Vib.Sensor === undefined) newData_Vib.Sensor = "Vibration(Frequency-X)";
        
        const regex = /Frequency X:\s*([0-9]+(\.[0-9]+)?)\s*Hz/;
        const match = currentData.match(regex);
        let value = 0;

        if(match) value = parseFloat(match[1]);

        if(name.includes('_01')){
          newData_Vib["Tunnel-1"] = value;
          sensors[5].lastMeasurement1 = time;
        }
        else{
          newData_Vib["Tunnel-2"] = value;
          sensors[5].lastMeasurement2 = time;
        }
      }
      else if(name.includes('LightSensor')){
        if(newData_Light.Sensor === undefined) newData_Light.Sensor = "LightSensor";
        
        const regex = /(\d+)\s*lux/;
        const match = currentData.match(regex);
        let value = 0;

        if(match) value = parseFloat(match[1]);

        if(name.includes('_01')){
          newData_Light["Tunnel-1"] = value;
          sensors[2].lastMeasurement1 = time;
        }
        else{
          newData_Light["Tunnel-2"] = value;
          sensors[2].lastMeasurement2 = time;
        }
      }
      else if(name.includes('Temperature')){
        if(newData_Temp.Sensor === undefined) newData_Temp.Sensor = "Temperature";
        
        const regex = /(\d+\.?\d*)\s*°\s*C/;
        const match = currentData.match(regex);
        let value = 0;

        if(match) value = parseFloat(match[1]);

        if(name.includes('_01')){
          newData_Temp["Tunnel-1"] = value;
          sensors[3].lastMeasurement1 = time;
        }
        else{
          newData_Temp["Tunnel-2"] = value;
          sensors[3].lastMeasurement2 = time;
        }
      }
      else if(name.includes('Tilt Detection')){
        if(newData_Tilt.Sensor === undefined) newData_Tilt.Sensor = "TiltDetection";
        
        const regex = /Pitch:\s*(-?\d+\.?\d*)\s*°/;
        const match = currentData.match(regex);
        let value = 0;

        if(match) value = parseFloat(match[1]);

        if(name.includes('_01')){
          newData_Tilt["Tunnel-1"] = value;
          sensors[4].lastMeasurement1 = time;
        }
        else{
          newData_Tilt["Tunnel-2"] = value;
          sensors[4].lastMeasurement2 = time;
        }
      }
      else if(name.includes('Water')){
        if(newData_Water.Sensor === undefined) newData_Water.Sensor = "WaterDetection";

        let value = 0;
        if(currentData !== "No Water Present") value = 1;

        if(name.includes('_01')){
          newData_Water["Tunnel-1"] = value;
          sensors[6].lastMeasurement1 = time;
        }
        else{
          newData_Water["Tunnel-2"] = value;
          sensors[6].lastMeasurement2 = time;
        }
      }
    }
    let max = getMaxSum([newData_Air, newData_Light, newData_Temp, newData_Tilt, newData_Vib]);
    newData_Act['Tunnel-1'] = max*newData_Act['Tunnel-1'];
    newData_Water['Tunnel-1'] = (max/2.0)*newData_Water['Tunnel-1'];
    newData_Water['Tunnel-2'] = (max/2.0)*newData_Water['Tunnel-2'];

    newLists.push(newData_Act, newData_Air, newData_Light, newData_Temp, newData_Tilt, newData_Vib, newData_Water);
    return {newData: newLists};
  }, [sensors]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GetRecentData();
        const {newData} = AllData(response.Result);

        setData(newData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [AllData]);

  return (
    <div>
      <Header title={title_str} subtitle={subtitle_str} />
      <Box className="main-box" backgroundColor={colors.primary[400]}>
        <Box className="main-box-detail">
          <Box>
            <Typography variant="h3" fontWeight="600" color={colors.grey[100]}>
              모든 센서 데이터
            </Typography>
            <Typography variant="h6" fontWeight="500" color={colors.greenAccent[400]}>
              총 13개 센서로부터 데이터를 받습니다.
            </Typography>
            <div>
              <Typography variant="h6" fontWeight="500" color={colors.grey[100]}>
                각 센서 설명
                <Button onClick={() => setExpanded(!expanded)} variant="contained" color="info" size="small" sx={{ml:2}}>
                  {expanded ? '접기' : '펼치기'}
                </Button>
              </Typography>
            </div>
            {expanded && (
              <div className="sensorDescription">
                {sensors.map((sensor, index) => (
                  <Box key={index} mb={2}>
                    <Typography variant="h6" fontWeight="500" color={colors.grey[200]}>
                      {sensor.name}
                    </Typography>
                    <Typography variant="body1" color={colors.grey[400]}>
                      <strong>역할:</strong> {sensor.description}
                    </Typography>
                    <Typography variant="body1" color={colors.grey[400]}>
                      <strong>단위:</strong> {sensor.unit}
                    </Typography>
                    <Typography variant="body1" color={colors.grey[400]}>
                      <strong>마지막 측정 시간:</strong> {sensor.lastMeasurement1} (Tunnel-1), {sensor.lastMeasurement2} (Tunnel-2)
                    </Typography>
                  </Box>
                ))}
              </div>
            )}
          </Box>
        </Box>
        <Box className="main-box-graph">
          {data && (
            <FormatLineChart
              data={data}
              is_dashboard={true}
              y_label={""}
            />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default Dashboard;
